import { BookOpen, FileText, LayoutDashboard, Presentation } from 'lucide-react'
import { useState } from 'react'
import { isHebrewLine } from '../../lib/hebrew'
import type { RouterOutputs } from '../../lib/trpc'
import './doc-cover.css'

export type StudioDoc = RouterOutputs['documents']['list'][number]

/**
 * Whether a document is a book's cover. A cover is drawn as its book, so it is
 * left out of every kind, count, folder preview and emptiness check, and is
 * found by asking for book covers.
 */
export const isCover = (studioDoc: Pick<StudioDoc, 'kind'>) =>
  studioDoc.kind === 'book-cover'

type SharedDoc = RouterOutputs['share']['sharedWithMe'][number]

/**
 * A document shared with this person, in the shape their own documents take.
 * What only the owner's listing knows — who edited it last, its folder, its
 * live links — is left empty rather than guessed.
 */
export const sharedDoc = (d: SharedDoc): StudioDoc => ({
  kind: d.kind,
  id: d.id,
  key: d.key,
  title: d.title,
  subtitle: d.subtitle,
  updatedAt: '',
  editedBy: null,
  liveLinks: 0,
  sharedWith: 0,
  slideCount: null,
  languages: null,
  coverImage: d.coverImage,
  coverFillsTile: d.coverFillsTile,
  coverOf: [],
  /* The owner's folder, named only where a shared folder is the way in, so
     the document is listed inside that folder. */
  folderId: d.folderId,
})

export const DOC_ICON = {
  book: BookOpen,
  page: FileText,
  deck: Presentation,
  board: LayoutDashboard,
  'book-cover': FileText,
} as const

/**
 * A document's cover: the document itself, small, lying on the canvas.
 *
 * The picture is drawn on the server, once per version of what it shows, and
 * its address names that version, so the browser keeps it and a shelf comes
 * back drawn. A document's own sheet is drawn whole, never cropped: a page
 * shows itself, a deck its first slide, a book the page it is covered by. A book with no such
 * page is drawn as its own title page, from the title and subtitle it holds.
 * A board has no sheet of its own, so it is drawn as a board: a few sheets
 * lying apart on a table, standing for what it holds without showing any.
 *
 * A cover designed for the document is drawn at the tile's own shape, so it
 * fills the tile rather than lying on it.
 *
 * Anything that cannot be drawn shows the document's kind instead of a blank.
 */
export function DocCover({
  studioDoc,
  small = false,
}: {
  studioDoc: StudioDoc
  /**
   * A tile in a folder's preview: less canvas around the sheet, and a title
   * page that holds the name alone.
   */
  small?: boolean
}) {
  const picture = studioDoc.coverImage
  const [failed, setFailed] = useState<string | null>(null)
  const [loaded, setLoaded] = useState<string | null>(null)
  return (
    <div
      className="doc-cover"
      aria-hidden
      data-size={small ? 'small' : undefined}
      data-fills={studioDoc.coverFillsTile || undefined}
    >
      {picture && failed !== picture ? (
        <div className="doc-cover__fit">
          <img
            className="doc-cover__sheet"
            src={picture}
            alt=""
            loading="lazy"
            decoding="async"
            draggable={false}
            data-loaded={loaded === picture || undefined}
            onLoad={() => setLoaded(picture)}
            onError={() => setFailed(picture)}
          />
        </div>
      ) : (
        <Undrawn studioDoc={studioDoc} />
      )}
    </div>
  )
}

/** What a document with no picture of itself shows in its place. */
function Undrawn({ studioDoc }: { studioDoc: StudioDoc }) {
  switch (studioDoc.kind) {
    case 'book':
      return <TitleOnCover studioDoc={studioDoc} />
    case 'board':
      return <BoardSchematic />
    case 'page':
    case 'deck':
    case 'book-cover':
      return <KindMark studioDoc={studioDoc} />
  }
}

/**
 * Sheets of three shapes lying apart, the way a board's items do. Drawn in a
 * box of its own proportions, so it is the same picture on every tile.
 */
const SCHEMATIC_SHEETS = [
  { x: 6, y: 10, width: 34, height: 22 },
  { x: 48, y: 6, width: 18, height: 26 },
  { x: 74, y: 14, width: 20, height: 14 },
  { x: 14, y: 40, width: 20, height: 28 },
  { x: 42, y: 42, width: 40, height: 24 },
] as const

function BoardSchematic() {
  return (
    <div className="doc-cover__fit">
      <svg
        className="doc-cover__board"
        viewBox="0 0 100 74"
        preserveAspectRatio="xMidYMid meet"
        aria-hidden="true"
      >
        {SCHEMATIC_SHEETS.map((sheet) => (
          <rect
            key={`${sheet.x}:${sheet.y}`}
            className="doc-cover__board-sheet"
            x={sheet.x}
            y={sheet.y}
            width={sheet.width}
            height={sheet.height}
          />
        ))}
      </svg>
    </div>
  )
}

/** A line wholly in Hebrew letters is Hebrew, whatever the book leads with. */
const lineLang = (text: string, book: 'he' | 'en') =>
  isHebrewLine(text) ? 'he' : book

function TitleOnCover({ studioDoc }: { studioDoc: StudioDoc }) {
  const hebrew = studioDoc.languages?.[0] === 'he'
  const book = hebrew ? 'he' : 'en'
  return (
    <div className="doc-cover__fit">
      <div
        className="doc-cover__book"
        lang={hebrew ? 'he' : 'en'}
        dir={hebrew ? 'rtl' : 'ltr'}
      >
        <span
          className="doc-cover__book-title"
          lang={lineLang(studioDoc.title, book)}
        >
          {studioDoc.title}
        </span>
        {studioDoc.subtitle ? (
          <span
            className="doc-cover__book-subtitle"
            lang={lineLang(studioDoc.subtitle, book)}
          >
            {studioDoc.subtitle}
          </span>
        ) : null}
      </div>
    </div>
  )
}

function KindMark({ studioDoc }: { studioDoc: StudioDoc }) {
  const Icon = DOC_ICON[studioDoc.kind]
  return (
    <div className="doc-cover__fit">
      <Icon className="doc-cover__mark" size={32} aria-hidden />
    </div>
  )
}
